<template>
	<div>
		<div class="download-bar">
			<download-btn
				alt="Descargar organigrama en PDF"
				url-image="/static/img/icons/pdf.svg"
				@download-item="downloadPdf"
			/>
		</div>
		<organization-chart-viewer ref="viewer" :area-id="areaId" />
		<div class="tw-text-center tw-mt-4">
			<v-btn class="custom-button black" @click="goBackNavigation">Atrás</v-btn>
			<v-btn class="custom-button" color="primary" @click="saveOrganizationChart">Guardar</v-btn>
		</div>
	</div>
</template>

<script>
import html2canvas from 'html2canvas';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import OrganizationChartViewer from '@/modules/job-evaluation/components/supplies/organization-chart/OrganizationChartViewer';
import DownloadBtn from '@/components/diagnosis/download-btn';
import bus from '@/bus/app-bus';
import helper from '@/shared/helper';

const LOADER_EVENT = 'app-event-loader-toggle-view';
const LOADER_MESSAGE = 'Praktiko esta cargando la jerarquía.';

function hexToRgb(hex) {
	return rgb(
		parseInt(hex.slice(1, 3), 16) / 255,
		parseInt(hex.slice(3, 5), 16) / 255,
		parseInt(hex.slice(5, 7), 16) / 255,
	);
}

export default {
	name: 'page-supply-organization-chart',
	components: { OrganizationChartViewer, DownloadBtn },
	computed: {
		areaId() {
			const areaId = this.$route.query.areaId;
			return areaId ? Number(areaId) : null;
		},
	},
	beforeDestroy() {
		bus.$emit(LOADER_EVENT, { show: false });
	},
	methods: {
		goBackNavigation() {
			bus.$emit(LOADER_EVENT, { show: true, message: LOADER_MESSAGE });
			// Yield two frames so the browser paints the overlay BEFORE the heavy,
			// synchronous route change (org-chart teardown + hierarchy render) blocks it.
			window.requestAnimationFrame(() => {
				window.requestAnimationFrame(() => {
					this.goBack();
				});
			});
		},
		saveOrganizationChart() {
			this.goTo('job-evaluation-supplies');
		},
		async downloadPdf() {
			bus.$emit(LOADER_EVENT, { show: true, message: 'Generando PDF del organigrama.' });
			// Yield two frames so the browser paints the loader BEFORE html2canvas blocks the main thread
			await new Promise(resolve =>
				window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)),
			);
			try {
				const viewer = this.$refs.viewer;
				const treeEl = viewer.$refs.treeWrapper.querySelector('.org-tree-container');
				if (!treeEl) throw new Error('Organigrama no disponible');

				// Expand scroll container so html2canvas captures the full tree, not just the viewport
				const scrollContainer = viewer.$el;
				const originalOverflow = scrollContainer.style.overflowX;
				const originalScrollLeft = scrollContainer.scrollLeft;
				scrollContainer.style.overflowX = 'visible';
				scrollContainer.scrollLeft = 0;

				// 1. Capture tree as high-res canvas
				const canvas = await html2canvas(treeEl, {
					scale: 2,
					backgroundColor: '#ffffff',
					useCORS: true,
					logging: false,
					width: treeEl.scrollWidth,
					height: treeEl.scrollHeight,
				});

				// Restore scroll container right after capture, before PDF processing
				scrollContainer.style.overflowX = originalOverflow;
				scrollContainer.scrollLeft = originalScrollLeft;

				// 2. Create PDF with auto-selected ISO page size
				const pngBase64 = canvas.toDataURL('image/png').split(',')[1];
				const pdfDoc = await PDFDocument.create();
				const pngImage = await pdfDoc.embedPng(pngBase64);

				const margin = 40;
				const glossaryHeight = 50;

				// Select smallest ISO landscape page where chart is readable (1:1 with screen)
				// Canvas was captured at scale 2, so screen size = pixel size / 2
				const targetW = pngImage.width / 2;
				const targetH = pngImage.height / 2;
				const isoLandscape = [
					{ w: 842, h: 595 }, // A4
					{ w: 1191, h: 842 }, // A3
					{ w: 1684, h: 1191 }, // A2
					{ w: 2384, h: 1684 }, // A1
				];
				const selectedSize = isoLandscape.find((size) => {
					const availW = size.w - (margin * 2);
					const availH = size.h - (margin * 2) - glossaryHeight - 55; // 55 = approx title block
					return (targetW <= availW) && (targetH <= availH);
				}) || isoLandscape[isoLandscape.length - 1]; // default to A1
				const pageWidth = selectedSize.w;
				const pageHeight = selectedSize.h;
				const page = pdfDoc.addPage([pageWidth, pageHeight]);

				// Embed fonts
				const titleFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
				const regFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

				// Resolve title data
				const companyName = (this.$userInfo && this.$userInfo.company)
					? this.$userInfo.company.name : '';
				let areaName = '';
				if (this.areaId) {
					const groups = this.$store.state.jobEvaluation.supplies.guidelineModel.groups || [];
					const areaGroup = groups.find(g => g.id === this.areaId);
					areaName = areaGroup ? areaGroup.name : '';
				}

				// 3. Draw title and subtitle (centered, top of page)
				const title = 'Organigrama de Valoración de Puestos';
				const titleSize = 16;
				const titleY = pageHeight - margin - titleSize;
				const titleW = titleFont.widthOfTextAtSize(title, titleSize);
				page.drawText(title, {
					x: (pageWidth - titleW) / 2,
					y: titleY,
					size: titleSize,
					font: titleFont,
					color: rgb(0.15, 0.15, 0.15),
				});

				let subtitle = companyName;
				if (areaName) subtitle = `${companyName} · Área ${areaName}`;
				const subSize = 11;
				const subY = titleY - subSize - 8;
				if (subtitle) {
					const subW = regFont.widthOfTextAtSize(subtitle, subSize);
					page.drawText(subtitle, {
						x: (pageWidth - subW) / 2,
						y: subY,
						size: subSize,
						font: regFont,
						color: rgb(0.35, 0.35, 0.35),
					});
				}

				// 4. Draw image centered vertically and horizontally in available space
				const titleSpace = titleSize + subSize + 28;
				const availableTop = pageHeight - margin - titleSpace;
				const availableBottom = glossaryHeight + margin;
				const availableH = availableTop - availableBottom;

				const imgBounds = pngImage.scaleToFit(
					pageWidth - (margin * 2),
					availableH,
				);
				page.drawImage(pngImage, {
					x: (pageWidth - imgBounds.width) / 2,
					y: availableBottom + ((availableH - imgBounds.height) / 2),
					width: imgBounds.width,
					height: imgBounds.height,
				});

				// 5. Draw glossary legend at the bottom
				const fontSize = 9;
				let gx = margin;
				const gy = 20;
				viewer.glossaryItem.forEach((item) => {
					page.drawRectangle({
						x: gx,
						y: gy,
						width: 12,
						height: 12,
						color: hexToRgb(item.color),
					});
					page.drawText(item.label, {
						x: gx + 16,
						y: gy + 2,
						size: fontSize,
						font: regFont,
						color: rgb(0.2, 0.2, 0.2),
					});
					gx += 16 + regFont.widthOfTextAtSize(item.label, fontSize) + 25;
				});

				// 5. Save and download
				const pdfBytes = await pdfDoc.save();
				const blob = helper.generateBlob(pdfBytes, 'application/pdf');
				helper.showDownloadDialog(blob, 'organigrama', 'pdf');
			} catch (error) {
				console.error('Error generating org chart PDF:', error);
			} finally {
				bus.$emit(LOADER_EVENT, { show: false });
			}
		},
	},
};
</script>

<style scoped>
	.download-bar {
		display: flex;
		justify-content: flex-end;
		padding: 0 16px;
	}
</style>



// WEBPACK FOOTER //
// src/modules/job-evaluation/pages/supplies/organization-chart/page-supply-organization-chart.vue